import { Controller } from "@hotwired/stimulus"

// Converts a server-rendered timestamp (ISO 8601 in UTC) into the user's
// local timezone using the browser's locale conventions.
//
// Server emits the raw element via the `local_time` helper:
//   <time datetime="2026-05-11T15:30:00Z"
//         data-controller="local-time"
//         data-local-time-format-value="datetime">
//     2026-05-11 15:30 UTC  ←  fallback for no-JS
//   </time>
//
// Formats supported via `data-local-time-format-value`:
//   "datetime"  → "May 11, 2026, 12:30 PM" (default)
//   "date"      → "May 11, 2026"
//   "short"     → "5/11/26, 12:30 PM"
//   "iso"       → "2026-05-11 12:30"  (Y-M-D H:M, no seconds, no tz)
export default class extends Controller {
  static values = {
    format: { type: String, default: "datetime" }
  }

  connect() {
    const iso = this.element.getAttribute("datetime")
    if (!iso) return

    const date = new Date(iso)
    if (isNaN(date.getTime())) return

    this.element.textContent = this.formatFor(date, this.formatValue)
    // Always set a hover tooltip with full date + local timezone for context.
    this.element.title = date.toLocaleString(undefined, {
      dateStyle: "full",
      timeStyle: "long"
    })
  }

  formatFor(date, format) {
    switch (format) {
      case "date":
        return date.toLocaleDateString(undefined, {
          year: "numeric", month: "short", day: "numeric"
        })
      case "short":
        return date.toLocaleString(undefined, {
          year: "2-digit", month: "numeric", day: "numeric",
          hour: "numeric", minute: "2-digit"
        })
      case "iso":
        // Pad helpers for a zero-config YYYY-MM-DD HH:MM rendering.
        const pad = (n) => String(n).padStart(2, "0")
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
      case "datetime":
      default:
        return date.toLocaleString(undefined, {
          year: "numeric", month: "short", day: "numeric",
          hour: "numeric", minute: "2-digit"
        })
    }
  }
}
